import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import { apiGet, apiPost } from 'src/api/client';
import { RootState } from 'src/store';
import { ProspectStatus, TypeCanal } from '../app/appSlice';
import { CustomResponseType, RequestStatus } from '../auth/authSlice';
import { UserCivilite, UserCountry, UserProcessing } from '../users/usersSlice';
import convertBase64Toblob from 'src/utils/covertBase64ToBlob';

export enum PoliticallyExposed {
  Y = 'Oui',
  N = 'Non'
}

export interface ProspectInformationResponse {
  clientBOA: boolean;
  uuid: string;
  civilite: UserCivilite;
  lastname: string;
  firstname: string;
  address: string;
  codePostale: string;
  city: string;
  country: {
    uuid: string;
    label_fr: string;
    iso2Alpha: UserCountry;
  };
  datedenaissance: string;
  nationality: {
    uuid: string;
    label_fr: string;
  };
  birthCountry: {
    uuid: string;
    label_fr: string;
  };
  birthCity: string;
}

export interface SourceOfIncomeResponse {
  uuid: string;
  amountofincome: string;
  sourceoffunds: {
    uuid: string;
    label_fr: string;
  };
  ibanClient: string;
  transfertMotif: {
    uuid: string;
    label_fr: string;
  };
  periodicity: {
    uuid: string;
    label_fr: string;
  };
  frequency: {
    uuid: string;
    label_fr: string;
  };
  revenuTranche: {
    uuid: string;
    label_fr: string;
  };
  nationalIdentifier?: string;
  transfervolume: string;
  activityArea: {
    uuid: string;
    label_fr: string;
  };
  profession: {
    uuid: string;
    label_fr: string;
  };
  politicallyexposed: string;
}

export interface BeneficiaryResponse {
  uuid: string;
  id: string;
  iban: string | null;
  codeSwift: string | null;
  sendToAmplitude: boolean;
  attestationRib?: string;
  beneficiareVerify: boolean;
  rejected?: boolean;
  banque: {
    uuid: string;
    label_fr: string;
    code: string;
  };
  relationFamiliale: {
    uuid: string;
    label_fr: string;
  };
  rib: string;
  savedDate: string;
  lastName: string;
  firstName: string;
  pays: {
    uuid: string;
    label_fr: string;
  };
}

type DocumentCode = '001' | '002' | '003' | '004' | '005';
export type StepType =
  | 'ETAPE1'
  | 'ETAPE2'
  | 'ETAPE3'
  | 'ETAPE4'
  | 'ETAPE5'
  | 'ETAPE6'
  | 'COMPLETED';

export interface Attachements {
  uuid: string;
  dateInserted: string;
  documentName: string;
  sentToAmplitude: boolean;
  deleted: boolean;
  documentType: {
    uuid: string;
    code: DocumentCode;
    dateInserted: string;
    recto: boolean;
    verso: boolean;
    label_es: string;
    label_it: string;
    label_fr: string;
  };
}

interface GetStepResponse extends CustomResponseType {
  step: StepType;
  dtProspect: ProspectInformationResponse;
  addDataProspect: SourceOfIncomeResponse;
  beneficiairesList: BeneficiaryResponse[];
  attachements: Attachements[];
  status: ProspectStatus;
  indicatif: string;
  countryCode: string;
  phone: string;
  email: string;
  idAmplitude: string;
  dateCreation: string;
  dateTransfert: string;
  montantTransfert: number;
  moyenTransfert: string;
  userProcessing: UserProcessing;
  blocked: boolean;
  disabled: boolean;
  fileNotSend: boolean;
  oldClient: boolean;
  canal: TypeCanal;
}

interface UserDetailsType {
  status: RequestStatus;
  details: GetStepResponse | null;
  logs: LogItem[];
  comments: CommentItem[];
}

const initialState: UserDetailsType = {
  status: 'idle',
  details: null,
  logs: [],
  comments: []
};

export interface LogItem {
  dateInserted: string;
  uuid: string;
  trace: string;
  nom: string;
  prenom: string;
}

export interface CommentItem {
  dateInserted: string;
  uuid: string;
  commentaire: string;
  nom: string;
  prenom: string;
}

/* fetch user details */
export const fetchUserDetails = createAsyncThunk<
  GetStepResponse,
  string,
  { rejectValue: CustomResponseType }
>('userDetails/getDetails', async (uuid: string, thunkApi) => {
  const response = await apiGet(`/customer/${uuid}`);
  if (response.data?.code === 'SUCCESS_CODE') {
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
    message: 'error'
  });
});

/* unblock user */
export const unblockUser = createAsyncThunk(
  'userDetails/unblockUser',
  async (uuid: string, thunkApi) => {
    const response = await apiPost('/customer/resetUser', { uuid });
    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(uuid));
      return response.data;
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'user non débloqué'
    });
  }
);

/* desactivated account */
export const desactivatedAccount = createAsyncThunk<
  CustomResponseType,
  string,
  { rejectValue: CustomResponseType }
>('userDetails/desactivatedAccount', async (uuid: string, thunkApi) => {
  const response = await apiPost('/customer/releaseUser', { uuid });
  if (response.data?.code !== 'SUCCESS_CODE') {
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'la désactivation du compte a échouéé'
    });
  }

  return response.data;
});
/* send-back beneficiary attachements */
type ResendBenefAttestationPayload = { userUuid: string };

export const sendBackBenefDocs = createAsyncThunk<
  CustomResponseType,
  ResendBenefAttestationPayload,
  { rejectValue: CustomResponseType }
>('userDetails/sendBackBenefDocs', async (payload: ResendBenefAttestationPayload, thunkApi) => {
  const response = await apiPost('/customer/renvoyRibToAmplitude', payload);
  if (response.data?.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(fetchUserDetails(payload.userUuid));
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
    message: 'échec lors de renvoie des documents'
  });
});

// resend back docs
export const sendBackDocs = createAsyncThunk<
  CustomResponseType,
  string,
  { rejectValue: CustomResponseType }
>('userDetails/sendBackDocs', async (uuid: string, thunkApi) => {
  const response = await apiPost('/customer/resendDocuments', { uuid });
  if (response.data?.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(fetchUserDetails(uuid));
    return response.data;
  }
  return thunkApi.rejectWithValue({
    code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
    message: 'échec lors de renvoie des documents'
  });
});

// download attachement
export const downloadAttachement = createAsyncThunk(
  'userDetails/getAttachement',
  async (payload: { uuid: string; documentName: string }) => {
    const { data } = await apiGet(`/documents/download/${payload.uuid}`, { responseType: 'blob' });
    saveAs(data, payload.documentName);
  }
);

// delete attchement
export const deleteAttachement = createAsyncThunk(
  'userDetails/deleteAttachement',
  async (payload: { uuid: string; uuidUser: string }, thunkApi) => {
    const response = await apiPost('/documents/delete', payload);
    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(payload.uuidUser));
      return response.data;
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'La modification est non sauvegardée'
    });
  }
);

// update step
export const updateStep = createAsyncThunk(
  'userDetails/updateStep',
  async (payload: { uuid: string; step: string }, thunkApi) => {
    const response = await apiPost('/customer/updateStep', payload);
    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(payload.uuid));
      return response.data;
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'La modification est non sauvegardée'
    });
  }
);

// update processing
interface UpdateProcessPayload {
  userUuid: string;
  processingStatusUuid: string;
  motifUuid: string;
}

// enum traitmentProgressErrorCode
enum TraitmentProgressErrCodes {
  COMPLETED_ERROR_PROCESSING,
  DATA_NOT_VALID,
  SUCCESS_CODE,
  INVALID_STEP,
  BENEF_RIB_NOT_VALID,
  DEFAULT_ERROR_MESSAGE
}

export type TraitmentProgressErrCodesType = keyof typeof TraitmentProgressErrCodes;

export type IUpdateProcessErrorCodes = {
  [key in TraitmentProgressErrCodesType]: string;
};

interface UpdateProcessErrorResponse extends CustomResponseType {
  code: TraitmentProgressErrCodesType;
}

export const updateProcessing = createAsyncThunk<
  CustomResponseType,
  UpdateProcessPayload,
  { rejectValue: UpdateProcessErrorResponse }
>(
  'userDetails/updateProcesisng',
  async (
    payload: { userUuid: string; processingStatusUuid: string; motifUuid: string },
    thunkApi
  ) => {
    const response = await apiPost('/customer/updateEtatTraitement', payload);
    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(payload.userUuid));
      return response.data;
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'La modification est non sauvegardéé '
    });
  }
);

// upload attchement
export const uploadAttachement = createAsyncThunk(
  'userDetails/uploadAttachement',
  async (payload: { fileData; uuidUser }, thunkApi) => {
    const response = await apiPost('/documents/upload', payload.fileData);
    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(payload.uuidUser));
      return response.data;
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'La modification est non sauvegardée'
    });
  }
);

// update info perso Client
export const updateInfoPersoClient = createAsyncThunk(
  'userDetails/updateInfoPersoClient',
  async (payload: { uuid; userInfoPerso }, thunkApi) => {
    const response = await apiPost(`/customer/update/${payload.uuid}`, payload.userInfoPerso);
    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(payload.uuid));
      return response.data;
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'Error lors de la telechargement de fichier'
    });
  }
);

// update info perso Client
type UpdatePhonePayload = { uuid: string; phone: string };
export const updateClientPhoneNumber = createAsyncThunk<
  CustomResponseType,
  UpdatePhonePayload,
  { rejectValue: CustomResponseType }
>('userDetails/updateClientPhoneNumber', async (payload: UpdatePhonePayload, thunkApi) => {
  const response = await apiPost('/customer/update', payload);
  if (response.data?.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(fetchUserDetails(payload.uuid));
    return response.data;
  }
  return thunkApi.rejectWithValue({
    code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
    message: 'Error lors de la telechargement de fichier'
  });
});

// update source of income
interface SourceIncomePayload {
  uuid: string;
  [key: string]: string;
}
export const updateSourceOfIncome = createAsyncThunk<
  CustomResponseType,
  SourceIncomePayload,
  { rejectValue: CustomResponseType }
>('userDetails/updateSourceOfIncome', async (payload: SourceIncomePayload, thunkApi) => {
  const response = await apiPost(`/customer/updateDeclaration/${payload.uuid} `, payload);
  if (response.data?.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(fetchUserDetails(payload.uuid));
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
    message: 'Error lors de la sauvegarde déclaration des revenues'
  });
});

interface BenefPayload {
  userUuid: string;
  [key: string]: string | boolean;
}

export const updateBeneficiary = createAsyncThunk<
  CustomResponseType,
  BenefPayload,
  { rejectValue: CustomResponseType }
>('userDetails/updateBeneficiary', async (payload: BenefPayload, thunkApi) => {
  const response = await apiPost(`/customer/updateBenef/${payload.userUuid} `, payload);
  if (response.data?.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(fetchUserDetails(payload.userUuid));
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
    message: 'Error lors de la sauvegarde des modifications'
  });
});

// upload beneficiary attachment
interface UploadBenefRibAttestaionPayload {
  attestationRib: string;
  uuidBenef: string;
  userUuid: string;
}
export const uploadBeneficiaryAttachement = createAsyncThunk<
  CustomResponseType,
  UploadBenefRibAttestaionPayload,
  { rejectValue: CustomResponseType }
>(
  'userDetails/uploadBenefAttachement',
  async (payload: UploadBenefRibAttestaionPayload, thunkApi) => {
    const response = await apiPost('/customer/updateRib', payload);
    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(payload.userUuid));
      return response.data;
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: "Erreur lors l'opération d'Upload"
    });
  }
);
// validate beneficiary attachment
type ValidateBenefAttachmentPayload = {
  userUuid: string;
  uuidBenef: string;
};

export const validateBenifAttachment = createAsyncThunk<
  CustomResponseType,
  ValidateBenefAttachmentPayload,
  { rejectValue: CustomResponseType }
>(
  'userDetails/validateBenifAttachment',
  async (payload: ValidateBenefAttachmentPayload, thunkApi) => {
    const response = await apiPost('/customer/validateRibBenefi', payload);

    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(fetchUserDetails(payload.userUuid));
      return { ...response.data, message: 'Le bénéficiaire a été validé avec succès !' };
    }
    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'Une erreur s’est produite lors de la validation du bénéficiaire !'
    });
  }
);

interface CutomerLogResponse extends CustomResponseType {
  logs: LogItem[];
}

interface UserCommentsResponse extends CustomResponseType {
  comments: CommentItem[];
}

// get customer logs
export const getCustomerLogs = createAsyncThunk<
  CutomerLogResponse,
  string,
  { rejectValue: CustomResponseType }
>('userDetails/history', async (uuid: string, thunkApi) => {
  const result = await apiGet(`/customer/history/${uuid}`);

  if (result?.data?.code === 'SUCCESS_CODE') {
    return result.data;
  }
  return thunkApi.rejectWithValue({
    code: result.data.code,
    message: 'Error lors du récupérations des logs'
  });
});

// get user comments
export const getUserComments = createAsyncThunk<
  UserCommentsResponse,
  string,
  { rejectValue: CustomResponseType }
>('userDetails/userComments', async (userUuid: string, thunkApi) => {
  const result = await apiGet(`/customer/comments/${userUuid}`);

  if (result?.data?.code === 'SUCCESS_CODE') {
    return result.data;
  }
  return thunkApi.rejectWithValue({
    code: result.data.code,
    message: 'Error lors du récupérations des commentaires'
  });
});

// add user comment
export const addUserComment = createAsyncThunk(
  'userDetails/addComment',
  async (payload: { userUuid: string; comment: string }, thunkApi) => {
    const response = await apiPost('/customer/addComment', payload);

    if (response.data?.code === 'SUCCESS_CODE') {
      thunkApi.dispatch(getUserComments(payload.userUuid));
      return response.data;
    }

    return thunkApi.rejectWithValue({
      code: response.data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: 'Error lors de la sauvegarde de commantaire'
    });
  }
);

interface DownloadBenefAttchmentPayload {
  uuid: string;
}
interface DownloadBenefAttchmentResponse extends CustomResponseType {
  attestationRib: string;
}
// download attachement for beneficiary
export const downloadBeneficiaryAttachement = createAsyncThunk<
  DownloadBenefAttchmentResponse,
  DownloadBenefAttchmentPayload,
  { rejectValue: CustomResponseType }
>(
  'userDetails/downloadBeneficiaryAttachement',
  async (payload: DownloadBenefAttchmentPayload, thunkApi) => {
    // Fetch the data from the API
    const { data } = await apiGet(`/customer/downloadRib/${payload.uuid}`);

    // Validate the response
    if (data.attestationRib && data.code === 'SUCCESS_CODE') {
      const result = convertBase64Toblob(data.attestationRib, 'beneficiary_attestation');
      saveAs(result.blob, result.fileName);
      return data;
    }

    return thunkApi.rejectWithValue({
      code: data?.code ?? 'ERR_BLOCKED_BY_WAF',
      message: "Erreur lors de le téléchargement d'attestaion Rib !"
    });
  }
);

type DeleteBeneficiaryPayload = {
  userUuid: string;
  uuidBenef: string;
};

// delete attachement for beneficiary
export const deleteBeneficiary = createAsyncThunk<
  CustomResponseType,
  DeleteBeneficiaryPayload,
  { rejectValue: CustomResponseType }
>('userDetails/deleteBeneficiary', async (payload: DeleteBeneficiaryPayload, thunkApi) => {
  // Fetch the data from the API
  const response = await apiPost('/customer/deleteBenef', payload);

  // Validate the response
  if (response.data?.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(fetchUserDetails(payload.userUuid));
    return { ...response.data, message: 'Le bénéficiare a été supprimé avec succès!' };
  }
  return thunkApi.rejectWithValue({
    code: response.data.code,
    message: 'Une erreur s’est produite lors de la suppression du bénéficiaire.'
  });
});

type RejectBeneficiaryPayload = {
  userUuid: string;
  uuidBenef: string;
};

// delete attachement for beneficiary
export const rejectBeneficiary = createAsyncThunk<
  CustomResponseType,
  RejectBeneficiaryPayload,
  { rejectValue: CustomResponseType }
>('userDetails/rejectBeneficiary', async (payload: RejectBeneficiaryPayload, thunkApi) => {
  // Fetch the data from the API
  const response = await apiPost('/customer/rejectBenef', payload);

  // Validate the response
  if (response.data?.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(fetchUserDetails(payload.userUuid));
    return { ...response.data, message: 'Le bénéficiare a été rejeté avec succès!' };
  }
  return thunkApi.rejectWithValue({
    code: response.data.code,
    message: 'Une erreur s’est produite lors du rejet du bénéficiaire.'
  });
});

const userDetailsSlice = createSlice({
  name: 'userDetails',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchUserDetails.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchUserDetails.fulfilled, (state, action) => {
      state.status = 'idle';

      state.details = action.payload;
    });
    builder.addCase(fetchUserDetails.rejected, (state) => {
      state.status = 'error';
      state.details = null;
    });
    builder.addCase(deleteAttachement.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(deleteAttachement.rejected, (state) => {
      state.status = 'error';
    });
    builder.addCase(updateStep.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(updateStep.rejected, (state) => {
      state.status = 'error';
    });
    builder.addCase(updateInfoPersoClient.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(updateInfoPersoClient.rejected, (state) => {
      state.status = 'error';
    });
    builder.addCase(updateClientPhoneNumber.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(updateClientPhoneNumber.rejected, (state) => {
      state.status = 'error';
    });
    builder.addCase(updateSourceOfIncome.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(updateSourceOfIncome.rejected, (state) => {
      state.status = 'error';
    });
    builder.addCase(updateBeneficiary.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(updateBeneficiary.rejected, (state) => {
      state.status = 'error';
    });
    builder.addCase(uploadAttachement.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(uploadAttachement.rejected, (state) => {
      state.status = 'error';
    });

    // uplaod benef attestation action
    builder.addCase(uploadBeneficiaryAttachement.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(uploadBeneficiaryAttachement.rejected, (state) => {
      state.status = 'error';
    });

    // validate benef action
    builder.addCase(validateBenifAttachment.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(validateBenifAttachment.rejected, (state) => {
      state.status = 'error';
    });

    // update processing action
    builder.addCase(updateProcessing.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(updateProcessing.rejected, (state) => {
      state.status = 'error';
    });

    // get customer logs action
    builder.addCase(getCustomerLogs.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(getCustomerLogs.fulfilled, (state, action) => {
      state.status = 'idle';
      state.logs = action.payload.logs;
    });

    builder.addCase(getCustomerLogs.rejected, (state) => {
      state.status = 'error';
    });

    // get comments action
    builder.addCase(getUserComments.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(getUserComments.fulfilled, (state, action) => {
      state.status = 'idle';
      state.comments = action.payload.comments;
    });

    builder.addCase(getUserComments.rejected, (state) => {
      state.status = 'error';
    });
    // add comment action
    builder.addCase(addUserComment.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(addUserComment.rejected, (state) => {
      state.status = 'error';
    });

    // unlock user action
    builder.addCase(unblockUser.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(unblockUser.rejected, (state) => {
      state.status = 'error';
    });

    //  deactivate account action
    builder.addCase(desactivatedAccount.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(desactivatedAccount.fulfilled, (state) => {
      state.status = 'idle';
    });

    builder.addCase(desactivatedAccount.rejected, (state) => {
      state.status = 'error';
    });

    // send back docs action
    builder.addCase(sendBackDocs.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(sendBackDocs.rejected, (state) => {
      state.status = 'error';
    });

    // download benef attestation action
    builder.addCase(downloadBeneficiaryAttachement.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(downloadBeneficiaryAttachement.rejected, (state) => {
      state.status = 'error';
    });

    builder.addCase(downloadBeneficiaryAttachement.fulfilled, (state) => {
      state.status = 'idle';
    });

    // send back benef docs action
    builder.addCase(sendBackBenefDocs.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(sendBackBenefDocs.rejected, (state) => {
      state.status = 'error';
    });

    builder.addCase(sendBackBenefDocs.fulfilled, (state) => {
      state.status = 'idle';
    });

    // delete action
    builder.addCase(deleteBeneficiary.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(deleteBeneficiary.fulfilled, (state) => {
      state.status = 'idle';
    });
    builder.addCase(deleteBeneficiary.rejected, (state) => {
      state.status = 'error';
    });

    // reject action
    builder.addCase(rejectBeneficiary.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(rejectBeneficiary.fulfilled, (state) => {
      state.status = 'idle';
    });
    builder.addCase(rejectBeneficiary.rejected, (state) => {
      state.status = 'error';
    });
  }
});

// select Request status
export const selectDetailsRequestStatus = (state: RootState) => state.userDetails.status;

// select details Status
export const selectDetailsStatus = (state: RootState) => state.userDetails.details?.status;

// select user status (blocked or not)
export const selectBlockedUser = (state: RootState) => state.userDetails.details?.blocked;

// select user status (disabled or not)
export const selectDisabledUser = (state: RootState) => state.userDetails.details?.disabled;

// select step
export const selectDetailsStep = (state: RootState) => state.userDetails.details?.step;

// select processing status
export const selectProcessingStatus = (state: RootState) =>
  state.userDetails.details?.userProcessing;

// select Beneficiaries list
export const selectBeneficiaries = (state: RootState) =>
  state.userDetails.details?.beneficiairesList ?? [];

// select Beneficiary by id
export const selectBeneficiaryById = (id: string) => (state: RootState) =>
  state.userDetails.details?.beneficiairesList?.find((benef) => benef.uuid === id) ?? {};

// select personal information
export const selectPersonalInformation = (state: RootState) =>
  state.userDetails.details?.dtProspect ?? {};

// select source income
export const selectSourceIncome = (state: RootState) =>
  state.userDetails.details?.addDataProspect ?? {};

// select attachements list
export const selectAttachements = (state: RootState) =>
  state.userDetails.details?.attachements ?? [];

// select user logs
export const selectUserLogs = (state: RootState) => state.userDetails.logs ?? [];

// select user comments
export const selectUserComments = (state: RootState) => state.userDetails.comments ?? [];

// General Info
export const selectGeneralInfoUser = (state: RootState) => {
  /* eslint-disable-next-line */
  const {
    indicatif,
    phone,
    email,
    idAmplitude,
    dateCreation,
    dateTransfert,
    montantTransfert,
    moyenTransfert,
    fileNotSend,
    oldClient,
    countryCode,
    canal
  } = state.userDetails?.details ?? {};
  return {
    indicatif,
    phone,
    email,
    idAmplitude,
    dateCreation,
    dateTransfert,
    montantTransfert,
    moyenTransfert,
    fileNotSend,
    oldClient,
    countryCode,
    canal
  };
};
// reducer
export default userDetailsSlice.reducer;
